import React, { useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { mergeWith, omit } from 'lodash'
import { Box, Button, Typography } from '@mui/material'
import { ZodTypeAny } from 'zod'
import { PatientHistoryType } from 'src/graphql.generated'
import { useDraftSaver, useRestoredDraft } from '../../../api/historyDraft'
import { useSubmitPatientHistory } from '../../../api/useSubmitPatientHistory'
import { useDisabledInPreview } from '../../../dashboard/panelPreviewContext'
import {
  CheckboxGroup,
  ConfirmNote,
  DraftNotice,
  FieldGrid,
  FieldSection,
  FormActions,
  LabeledCheckbox,
  LabeledDate,
  LabeledNumber,
  LabeledRadioGroup,
  LabeledText,
  LabeledTime,
  LabeledValue,
  REQUIRED,
  todayIso,
} from '../../fieldPrimitives'
import { BillingCode } from './billingCodes'
import BillingCodesSelect from './BillingCodesSelect'
import { Field, Section } from '../historyLayoutKit'
import { Problem, ValidationIssues, validationProblems } from './validation'
import { getIn, isFilled, setIn } from './valuePath'

type Values = Record<string, unknown>

type Props = {
  uid: string
  type: PatientHistoryType
  sections: Section[]
  schema?: ZodTypeAny
  // Defaults and prefilled values. A new `key` on this component re-seeds it.
  initial?: Values
  // The day the entry is dated when no draft says otherwise; today if omitted.
  initialDate?: string
  // Standard keeps the entry date inside `value` too, and its server-side readers look for it there.
  dateInValue?: boolean
  // Standard asks for no date on some notes; they are dated the day they are written.
  showDate?: boolean
  confirm?: ConfirmNote
}

const pathOf = (field: Field) => (Array.isArray(field.path) ? field.path[0] : field.path)

// A checkbox is never missing from a saved entry: the export rejects a group with an absent key.
export const unticked = (sections: Section[]): Values =>
  sections
    .flatMap((section) => section.fields)
    .reduce<Values>((empty, field) => {
      if (field.kind === 'yesno') {
        return setIn(empty, pathOf(field), false)
      }

      return field.kind === 'checks'
        ? field.options.reduce((acc, option) => setIn(acc, `${pathOf(field)}.${option.key}`, false), empty)
        : empty
    }, {})

// An empty form, as the schema first sees it: what fails here is what Standard requires.
const blank = (sections: Section[]): Values =>
  sections
    .flatMap((section) => (section.readOnly ? [] : section.fields))
    .reduce<Values>((empty, field) => {
      switch (field.kind) {
        case 'text':
        case 'date':
        case 'choice':
          return setIn(empty, pathOf(field), '')
        case 'value':
          return field.input === 'number' ? empty : setIn(empty, pathOf(field), '')
        case 'codes':
        case 'rows':
          return setIn(empty, pathOf(field), [])
        default:
          return empty
      }
    }, unticked(sections))

const pathsOf = (field: Field) => (Array.isArray(field.path) ? field.path : [field.path])

const isOn = (path: string, own: string) => path === own || path.startsWith(`${own}.`) || own.startsWith(`${path}.`)

const isShort = (field: Field) => field.kind === 'date' || field.kind === 'value'

const inRows = (fields: Field[]): Field[][] =>
  fields.reduce<Field[][]>((rows, field) => {
    const last = rows[rows.length - 1]

    if (isShort(field) && last && isShort(last[0])) {
      last.push(field)
    } else {
      rows.push([field])
    }

    return rows
  }, [])

const asString = (candidate: unknown): string => (typeof candidate === 'string' ? candidate : '')

const RowsEditor = ({
  field,
  rows,
  onChange,
}: {
  field: Extract<Field, { kind: 'rows' }>
  rows: Values[]
  onChange: (next: Values[]) => void
}) => {
  const disabled = useDisabledInPreview()
  const shown = rows.length > 0 ? rows : [{}]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="overline">{field.label}</Typography>
      {shown.map((row, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={index} sx={{ display: 'grid', gridTemplateColumns: `repeat(${field.columns.length}, 1fr)`, gap: 1 }}>
          {field.columns.map((column) => (
            <LabeledText
              key={column.key}
              label={column.label}
              value={asString(row[column.key])}
              onChange={(next) =>
                onChange(shown.map((item, at) => (at === index ? { ...item, [column.key]: next } : item)))
              }
            />
          ))}
          {index > 0 && (
            <Button
              size="small"
              color="error"
              disabled={disabled}
              onClick={() => onChange(shown.filter((_item, at) => at !== index))}
            >
              Remove
            </Button>
          )}
        </Box>
      ))}
      <Box>
        <Button size="small" disabled={disabled} onClick={() => onChange([...shown, {}])}>
          Add another
        </Button>
      </Box>
    </Box>
  )
}

const FieldInput = ({
  field,
  values,
  set,
  readOnly,
  date,
  required,
  error,
  sectionTitle,
}: {
  field: Field
  values: Values
  set: (path: string, next: unknown) => void
  readOnly: boolean
  date: string
  required: boolean
  error?: string
  sectionTitle?: string
}) => {
  const path = pathOf(field)
  const current = getIn(values, path)

  if (readOnly) {
    return <LabeledValue label={field.label} value={isFilled(current) ? String(current) : '—'} />
  }

  switch (field.kind) {
    case 'text':
      return (
        <LabeledText
          label={field.label}
          value={asString(current)}
          onChange={(next) => set(path, next)}
          helperText={field.hint}
          placeholder={field.placeholder}
          required={required}
          error={error}
          multiline
        />
      )
    case 'value':
      if (field.input === 'number') {
        return (
          <LabeledNumber
            label={field.label}
            value={typeof current === 'number' ? current : ''}
            onChange={(next) => set(path, next === '' ? undefined : next)}
            required={required}
            error={error}
          />
        )
      }

      return field.input === 'datetime' ? (
        <LabeledTime
          label={field.label}
          value={asString(current) ? DateTime.fromISO(asString(current)).toFormat('HH:mm') : ''}
          onChange={(next) => set(path, timeOnDate(date, next))}
          required={required}
          error={error}
        />
      ) : (
        <LabeledText
          label={field.label}
          value={asString(current)}
          onChange={(next) => set(path, next)}
          required={required}
          error={error}
        />
      )
    case 'yesno':
      return (
        <LabeledCheckbox
          label={field.label}
          checked={current === true}
          onChange={(next) => set(path, next)}
          helperText={field.hint}
          required={required}
          error={error}
        />
      )
    case 'date':
      return (
        <LabeledDate
          label={field.label}
          value={asString(current)}
          onChange={(next) => set(path, next)}
          required={required}
          error={error}
        />
      )
    case 'choice':
      return (
        <LabeledRadioGroup
          label={field.label || sectionTitle || ''}
          value={asString(current)}
          options={[...field.options]}
          onChange={(next) => set(path, next)}
          required={required}
          error={error}
        />
      )
    case 'checks': {
      const group = (typeof current === 'object' && current !== null ? current : {}) as Values

      return (
        <>
          <CheckboxGroup
            title={field.label}
            options={[...field.options]}
            values={group as Record<string, boolean>}
            onChange={(key, checked) => set(`${path}.${key}`, checked)}
          />
          {field.otherLabel && (
            <LabeledText
              label={field.otherLabel}
              value={asString(group.OTHER)}
              onChange={(next) => set(`${path}.OTHER`, next)}
              multiline
            />
          )}
        </>
      )
    }
    case 'codes':
      return (
        <BillingCodesSelect
          label={field.label || undefined}
          value={(Array.isArray(current) ? current : []) as BillingCode[]}
          onChange={(next) => set(path, next)}
          required={required}
          error={error}
        />
      )
    case 'rows':
      return (
        <RowsEditor
          field={field}
          rows={(Array.isArray(current) ? current : []) as Values[]}
          onChange={(next) => set(path, next)}
        />
      )
    default:
      return null
  }
}

const timeOnDate = (date: string, time: string) =>
  time ? DateTime.fromISO(`${date}T${time}`).toUTC().toISO() ?? '' : ''

const replaceArrays = (_current: unknown, incoming: unknown) => (Array.isArray(incoming) ? incoming : undefined)

const SpecForm = ({
  uid,
  type,
  sections,
  schema,
  initial = {},
  initialDate,
  dateInValue = true,
  showDate = true,
  confirm,
}: Props) => {
  const restored = useRestoredDraft(uid, type)
  const [date, setDate] = useState(() => (restored && asString(restored.date)) || initialDate || todayIso())
  const [values, setValues] = useState<Values>(() =>
    restored
      ? mergeWith({}, unticked(sections), initial, omit(restored, 'date'), replaceArrays)
      : mergeWith({}, unticked(sections), initial)
  )
  const [attempted, setAttempted] = useState(false)
  const { submit, submitting, error } = useSubmitPatientHistory(uid)

  const set = (path: string, next: unknown) => setValues((current) => setIn(current, path, next))

  const timePaths = sections
    .flatMap((section) => section.fields)
    .filter((field) => field.kind === 'value' && field.input === 'datetime')
    .map(pathOf)
  const withDate = (current: Values) => {
    const dated = timePaths.reduce((acc, path) => {
      const stored = asString(getIn(acc, path))

      return stored ? setIn(acc, path, timeOnDate(date, DateTime.fromISO(stored).toFormat('HH:mm'))) : acc
    }, current)

    return dateInValue ? { ...dated, date } : dated
  }

  const dirty = useDraftSaver(uid, type, { ...withDate(values), date })

  const requiredPaths = useMemo(
    () => (schema ? validationProblems(schema, { ...blank(sections), ...(dateInValue && { date: todayIso() }) }) : []),
    [schema, sections, dateInValue]
  ).map((problem) => problem.path)
  const isRequired = (field: Field) => pathsOf(field).some((own) => requiredPaths.some((path) => isOn(path, own)))

  const problems: Problem[] = attempted && schema ? validationProblems(schema, withDate(values), sections) : []
  const errorFor = (field: Field) =>
    problems.some((problem) => pathsOf(field).some((own) => isOn(problem.path, own))) ? REQUIRED : undefined
  const issues = [...new Set(problems.map(({ label, message }) => `${label}: ${message}`))]

  const onSubmit = async () => {
    setAttempted(true)

    // The raw values go up, not the parsed ones: zod would drop keys the server's export reads.
    const value = withDate(values)

    if ((showDate && !date) || (schema && validationProblems(schema, value, sections).length > 0)) {
      return
    }

    try {
      await submit({ date, type, value })
      setValues(mergeWith({}, unticked(sections), initial))
      setAttempted(false)
    } catch {
      // The hook's `error` already carries the message the form shows.
    }
  }

  return (
    <FieldSection>
      {showDate && (
        <FieldGrid>
          <LabeledDate
            label="Date of event"
            value={date}
            onChange={setDate}
            required
            error={attempted && !date ? REQUIRED : undefined}
          />
        </FieldGrid>
      )}
      {sections.map((section, index) => {
        const fields = section.fields.filter((field) => field.when?.(values) !== false)

        return fields.length === 0 ? null : (
          <FieldSection key={section.title ?? index} title={section.title}>
            {inRows(fields).map((row) => {
              const inputs = row.map((field) => (
                <FieldInput
                  key={`${field.label}:${pathOf(field)}`}
                  field={field}
                  values={values}
                  set={set}
                  readOnly={Boolean(section.readOnly)}
                  date={date}
                  required={isRequired(field)}
                  error={errorFor(field)}
                  sectionTitle={section.title}
                />
              ))

              const key = `${row[0].label}:${pathOf(row[0])}`

              return row.length > 1 ? (
                <FieldGrid key={key}>{inputs}</FieldGrid>
              ) : (
                <React.Fragment key={key}>{inputs}</React.Fragment>
              )
            })}
          </FieldSection>
        )
      })}
      <ValidationIssues issues={issues} />
      <DraftNotice restored={restored !== null} dirty={dirty} />
      <FormActions onSubmit={onSubmit} disabled={false} submitting={submitting} error={error} confirm={confirm} />
    </FieldSection>
  )
}

export default SpecForm
